import React from "../../../../lib/teact/teact";
import { MessageReport } from "../../db/report/totalReport";
import { tienFormat } from "../../utils/NumberUtils";
import { Remove2C3C4C } from "../../utils/common";
import { GetTotalLaiLoText } from "../templete/reportTemplete";

type ReportItem = {
  groupName?: string;
  loaiCuocId?: string;
  xac: number;
  quaCo: number;
  trung: number;
  thuong: number;
  children?: ReportItem[];
};

type RenderRow = {
  kind: "group" | "detail";
  label: string;
  xac: number;
  thirdValue: number;
  trung: number;
  thuong: number;
};

export type TotalReportRenderOptions = {
  showDetails: boolean;
  renderTienCo: boolean;
  isCuPhapMn: boolean;
  isCuPhapMb: boolean;
};

export type TotalReportViewModel = {
  secondHeader: string;
  thirdHeader: string;
  rows: RenderRow[];
  totalXac: number;
  totalQuaCo: number;
  totalThuong: number;
  laiLoText: string;
  isLai: boolean;
};

function getTienCo(item: Pick<ReportItem, "xac" | "quaCo">): number {
  return item.xac - item.quaCo;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function buildTotalReportViewModel(
  data: MessageReport & { children?: ReportItem[] },
  options: TotalReportRenderOptions,
): TotalReportViewModel {
  let totalXac = 0;
  let totalQuaCo = 0;
  let totalThuong = 0;
  const rows: RenderRow[] = [];

  const children = data.children || [];
  children.forEach((item) => {
    totalXac += item.xac;
    totalQuaCo += item.quaCo;
    totalThuong += item.thuong;

    let groupName = item.groupName || "";
    if (options.isCuPhapMb && groupName === "XMB") {
      groupName = "Xiên";
    }

    rows.push({
      kind: "group",
      label: groupName,
      xac: item.xac,
      thirdValue: options.renderTienCo ? getTienCo(item) : item.quaCo,
      trung: item.trung,
      thuong: item.thuong,
    });

    if (options.showDetails && item.children) {
      item.children.forEach((child) => {
        rows.push({
          kind: "detail",
          label: Remove2C3C4C(child.loaiCuocId || ""),
          xac: child.xac,
          thirdValue: options.renderTienCo ? getTienCo(child) : child.quaCo,
          trung: child.trung,
          thuong: child.thuong,
        });
      });
    }
  });

  return {
    secondHeader: options.isCuPhapMn ? "Xác" : "Điểm",
    thirdHeader: options.isCuPhapMn ? (options.renderTienCo ? "T.Cò" : "Qua cò") : "Tiền",
    rows,
    totalXac,
    totalQuaCo,
    totalThuong,
    laiLoText: GetTotalLaiLoText(data),
    isLai: data.laiLo >= 0,
  };
}

export function renderTotalReportTable(
  viewModel: TotalReportViewModel,
  onThirdHeaderClick?: (e: React.MouseEvent<HTMLTableCellElement>) => void,
): JSX.Element {
  return (
    <table className="table-total">
      <tr>
        <td className="table-header">Loại</td>
        <td className="table-header">{viewModel.secondHeader}</td>
        <td className="table-header" onClick={onThirdHeaderClick}>
          {viewModel.thirdHeader}
        </td>
        <td className="table-header">Trúng</td>
        <td className="table-header">Thưởng</td>
      </tr>
      {viewModel.rows.map((row, index) => (
        <tr key={`${row.kind}-${index}`}>
          <td className={row.kind === "group" ? "group-row-data loaicuoc" : "loaicuoc-row-data"}>
            {row.label}
          </td>
          <td className={row.kind === "group" ? "group-row-data" : "loaicuoc-row-data"}>
            {tienFormat(row.xac, 1)}
          </td>
          <td className={row.kind === "group" ? "group-row-data" : "loaicuoc-row-data"}>
            {tienFormat(row.thirdValue, 1)}
          </td>
          <td
            className={`${row.kind === "group" ? "group-row-data" : "loaicuoc-row-data"}${
              row.trung !== 0 ? " trung" : ""
            }`}
          >
            {tienFormat(row.trung, 1)}
          </td>
          <td
            className={`${row.kind === "group" ? "group-row-data" : "loaicuoc-row-data"}${
              row.trung !== 0 ? " trung" : ""
            }`}
          >
            {tienFormat(row.thuong, 1)}
          </td>
        </tr>
      ))}
      <tr>
        <td className="total-row">Cộng</td>
        <td className="total-row">{tienFormat(viewModel.totalXac, 1)}</td>
        <td className="total-row">
          {tienFormat(
            viewModel.thirdHeader === "T.Cò"
              ? viewModel.totalXac - viewModel.totalQuaCo
              : viewModel.totalQuaCo,
            1,
          )}
        </td>
        <td colSpan={2} className="total-row">
          {tienFormat(viewModel.totalThuong, 1)}
        </td>
      </tr>
      <tr>
        <td colSpan={5} className={viewModel.isLai ? "basic-lailo lai" : "basic-lailo lo"}>
          {viewModel.laiLoText}
        </td>
      </tr>
    </table>
  );
}

export function buildTotalReportHtml(viewModel: TotalReportViewModel): string {
  const rowsHtml = viewModel.rows
    .map((row) => {
      const baseClass = row.kind === "group" ? "group-row-data" : "loaicuoc-row-data";
      const trungClass = row.trung !== 0 ? " trung" : "";
      return `
      <tr>
        <td class="${baseClass}${row.kind === "group" ? " loaicuoc" : ""}">${escapeHtml(row.label)}</td>
        <td class="${baseClass}">${tienFormat(row.xac, 1)}</td>
        <td class="${baseClass}">${tienFormat(row.thirdValue, 1)}</td>
        <td class="${baseClass}${trungClass}">${tienFormat(row.trung, 1)}</td>
        <td class="${baseClass}${trungClass}">${tienFormat(row.thuong, 1)}</td>
      </tr>`;
    })
    .join("");

  const totalThirdValue =
    viewModel.thirdHeader === "T.Cò" ? viewModel.totalXac - viewModel.totalQuaCo : viewModel.totalQuaCo;

  return `
  <table class="table-total">
    <tr>
      <td class="table-header">Loại</td>
      <td class="table-header">${viewModel.secondHeader}</td>
      <td class="table-header">${viewModel.thirdHeader}</td>
      <td class="table-header">Trúng</td>
      <td class="table-header">Thưởng</td>
    </tr>
    ${rowsHtml}
    <tr>
      <td class="total-row">Cộng</td>
      <td class="total-row">${tienFormat(viewModel.totalXac, 1)}</td>
      <td class="total-row">${tienFormat(totalThirdValue, 1)}</td>
      <td class="total-row" colspan="2">${tienFormat(viewModel.totalThuong, 1)}</td>
    </tr>
    <tr>
      <td colspan="5" class="basic-lailo ${viewModel.isLai ? "lai" : "lo"}">${escapeHtml(viewModel.laiLoText)}</td>
    </tr>
  </table>`;
}
